import { useEffect, useRef } from "react";

import {
  notificationsSocketService,
  type WalletBalanceSocketPayload,
} from "@/modules/dashboard/notifications/services/notifications-socket.service";

import type { RuntimeWallet } from "../types/payments-wallets.types";

/**
 * Live wallet balances pushed by the runtime (`wallet:balance_updated`) — subscribes to the notifications
 * singleton (rule 10: never open a second socket) and hands each payload to the caller.
 *
 * Advisory channel: the REST list stays the source of truth, so a lost event only leaves a screen one refresh
 * behind. The callback is kept in a ref so a caller's inline arrow never resubscribes.
 */
export function useWalletBalanceSocket(
  onBalance: (payload: WalletBalanceSocketPayload) => void,
  enabled = true,
): void {
  const onBalanceRef = useRef(onBalance);
  useEffect(() => {
    onBalanceRef.current = onBalance;
  });

  useEffect(() => {
    if (!enabled) return;
    return notificationsSocketService.onWalletBalanceUpdate((payload) => {
      onBalanceRef.current(payload);
    });
  }, [enabled]);
}

/**
 * Folds a pushed balance into a wallet list.
 *
 * UPSERT, not patch: `getOrCreate` can materialize a wallet in a currency this list has never seen. Events
 * older than what we already hold are dropped — the server serializes the writers, but their emits can still
 * arrive out of order. Returns the SAME array reference when nothing changed, so a memoized card does not
 * re-render and the per-wallet activity fetch does not re-fire.
 */
export function applyWalletBalance(
  wallets: RuntimeWallet[],
  payload: WalletBalanceSocketPayload,
): RuntimeWallet[] {
  const index = wallets.findIndex((wallet) => wallet.id === payload.id);

  if (index === -1) {
    return [...wallets, payload as unknown as RuntimeWallet];
  }

  const current = wallets[index];
  if (current.updated_at && payload.updated_at <= current.updated_at) return wallets;

  const next = [...wallets];
  next[index] = {
    ...current,
    balance_available: payload.balance_available,
    balance_pending: payload.balance_pending,
    balance_blocked: payload.balance_blocked,
    status: payload.status as RuntimeWallet["status"],
    last_movement_at: payload.last_movement_at,
    updated_at: payload.updated_at,
  };
  return next;
}
